import { FC, useCallback, useEffect, useId, useState } from 'react'
import { Dialog, Markdown, TextField } from '../atoms'
import { useAppDispatch, useAppSelector } from '../../lib/store'
import { Button, Stack, Typography } from '@mui/material'
import { saveGuestZip, toast } from '../../lib/utils'
import { InfoUsageDisclaimer } from './InfoUsageDisclaimer'
import { saveProfileChangesThunk, selectUser } from '../../lib/slices'
import { IoAlertCircle, IoCheckmarkCircle } from 'react-icons/io5'
import {
    errorColor,
    primaryIconSize,
    successColor,
} from '../../lib/styles/universal'
import { isPostalCode } from 'validator'
import { shallowEqual } from 'react-redux'
import { zipCodeAlertText, ZipCodeAlertTextConfig } from '../../i18n'
import { LanguageCode } from '@hazadapt-git/public-core-base'

interface ZipCodeAlertProps {
    message?: string
    onSubmit?(): void
    open: boolean
    onClose(): void
    lang?: LanguageCode
}

export const ZipCodeAlert: FC<ZipCodeAlertProps> = ({
    message,
    onSubmit,
    open,
    onClose,
    lang,
}) => {
    const dispatch = useAppDispatch()
    const user = useAppSelector(selectUser, shallowEqual)
    const formId = useId()
    const [zip, setZip] = useState('')
    const [error, setError] = useState<string>()

    const modalConfig: ZipCodeAlertTextConfig | undefined =
        zipCodeAlertText.get(lang ?? LanguageCode.ENGLISH)

    const handleZipChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            setZip(value)
            setError(undefined)
        },
        []
    )

    const handleGuestZipSave = async (zip: string) => {
        // Save to localStorage for guests
        const saved = saveGuestZip(zip)
        if (!saved) {
            throw new Error('Failed to save ZIP to localStorage')
        }
    }

    const submitZipCode = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()

            if (!isPostalCode(zip, 'any')) {
                setError('Invalid ZIP or postal code.')
                return
            }

            try {
                if (user) {
                    await dispatch(saveProfileChangesThunk({ zip })).unwrap()
                } else {
                    await handleGuestZipSave(zip)
                }

                toast(
                    'Thanks!',
                    <IoCheckmarkCircle
                        color={successColor}
                        size={primaryIconSize}
                    />
                )
                onClose()
                onSubmit?.()
            } catch {
                toast(
                    'We were unable to save your ZIP code. Please try again.',
                    <IoAlertCircle color={errorColor} size={primaryIconSize} />
                )
            }
        },
        [zip, dispatch, onSubmit, onClose, user]
    )

    useEffect(() => {
        if (open) {
            setZip('')
            setError(undefined)
        }
    }, [open])

    return modalConfig ? (
        <Dialog
            title={modalConfig.title}
            content={
                <Stack
                    component="form"
                    id={formId}
                    onSubmit={submitZipCode}
                    gap="1rem"
                >
                    <Typography>
                        {message ? (
                            message
                        ) : (
                            <Markdown content={modalConfig.body} />
                        )}
                    </Typography>
                    <Stack gap="0.5rem">
                        <TextField
                            value={zip}
                            onChange={handleZipChange}
                            placeholder={modalConfig.placeholder}
                            fullWidth
                            type="text"
                            variant="outlined"
                            error={!!error}
                        />
                        {error && (
                            <Typography color="error">{error}</Typography>
                        )}
                    </Stack>
                    <InfoUsageDisclaimer lang={lang} />
                </Stack>
            }
            open={open}
            onClose={onClose}
            ctas={[
                <Button
                    key="confirm"
                    variant="contained"
                    type="submit"
                    form={formId}
                    disabled={!zip}
                >
                    {modalConfig.cta}
                </Button>,
            ]}
        />
    ) : null
}
